import { Button } from '@execonline-inc/execonline-ui';
import { useQueryClient } from '@tanstack/react-query';
import { Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { NotTranslated, T } from '../../../../Translations';
import { launchTestSignIn, serverErrorMessage } from '../../Api';
import { formatDate, formatDateTime } from '../../dates';
import StatusChip from '../../StatusChip';
import { ConnectionResource } from '../../Types';
import UpdateMetadataUrl from './UpdateMetadataUrl';

interface Props {
  resource: ConnectionResource;
}

type State =
  | { kind: 'viewing' }
  | { kind: 'launching' }
  // priorLaunchAtMs remembers the last test time on screen at launch; the
  // waiting state ends when the polled resource carries a newer one.
  | { kind: 'test-launched'; priorLaunchAtMs: number }
  | { kind: 'updating-metadata' }
  | { kind: 'error'; message: Maybe<string> };

const ConnectionCard: React.FC<Props> = ({ resource }) => {
  const [state, setState] = React.useState<State>({ kind: 'viewing' });
  const queryClient = useQueryClient();

  const lastLaunchAtMs = resource.payload.lastLaunchAt.map((at) => at.getTime()).getOrElseValue(-1);

  const waitingForResults =
    state.kind === 'test-launched' && state.priorLaunchAtMs === lastLaunchAtMs;

  // The connection query only polls during the wizard statuses, and the app
  // disables refetch-on-focus globally - so on an active connection, poll
  // here until the launched test's results land. Same as AttributesCheck.
  React.useEffect(() => {
    if (!waitingForResults) {
      return;
    }
    const interval = window.setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['sso-configuration'] });
    }, 5000);
    return () => window.clearInterval(interval);
  }, [waitingForResults, queryClient]);

  const runTest = () => {
    setState({ kind: 'launching' });
    launchTestSignIn(resource.links).fork(
      (err) => setState({ kind: 'error', message: serverErrorMessage(err) }),
      () => setState({ kind: 'test-launched', priorLaunchAtMs: lastLaunchAtMs }),
    );
  };

  return (
    <div>
      <h3 className="text-base font-semibold text-foreground">
        <T kind="Connection" />
      </h3>
      <div className="mt-3 rounded-lg border border-border bg-card p-6">
        <dl className="m-0 grid grid-cols-[10rem_1fr] gap-x-4 gap-y-3 text-sm">
          <dt className="text-muted-foreground">
            <T kind="Status" />
          </dt>
          <dd className="m-0">
            <StatusChip status={resource.payload.status} />
          </dd>
          <dt className="text-muted-foreground">
            <T kind="Environment" />
          </dt>
          <dd className="m-0 text-foreground">
            {resource.payload.environment === 'production' ? (
              <T kind="Production" />
            ) : (
              <T kind="Staging / test" />
            )}
          </dd>
          <dt className="text-muted-foreground">
            <T kind="Metadata URL" />
          </dt>
          <dd className="m-0 break-all font-mono text-xs text-foreground">
            {resource.payload.metadataUrl
              .map((url) => <span key="url">{url}</span>)
              .getOrElse(() => (
                <span key="url" className="font-sans text-muted-foreground">
                  <T kind="Not provided" />
                </span>
              ))}
          </dd>
          <dt className="text-muted-foreground">
            <T kind="Metadata verified" />
          </dt>
          <dd className="m-0 text-foreground">
            {resource.payload.verifiedAt
              .map((at) => <span key="at">{formatDateTime(at)}</span>)
              .getOrElse(() => (
                <span key="at" className="text-muted-foreground">
                  &mdash;
                </span>
              ))}
          </dd>
          {/* No IdP certificate row: nothing populates certificate_expires_at
              yet. Cognito exposes its own SP certificate, not the IdP's, so
              the expiry has to come from parsing the SAML metadata. */}
          <dt className="text-muted-foreground">
            <T kind="Created" />
          </dt>
          <dd className="m-0 text-foreground">{formatDate(resource.payload.createdAt)}</dd>
          <dt className="text-muted-foreground">
            <T kind="Last test" />
          </dt>
          <dd className="m-0 text-foreground">
            {resource.payload.lastLaunchAt
              .map((at) => <span key="at">{formatDateTime(at)}</span>)
              .getOrElse(() => (
                <span key="at" className="text-muted-foreground">
                  &mdash;
                </span>
              ))}
          </dd>
        </dl>
        {state.kind === 'error' && (
          <p className="mt-3 text-sm text-destructive">
            {state.message
              .map((text) => <NotTranslated key="server" text={text} />)
              .getOrElse(() => (
                <T kind="We are unable to process your request at this time" />
              ))}
          </p>
        )}
        {waitingForResults && (
          <p className="mt-3 text-sm text-muted-foreground">
            <T kind="Waiting for your test sign-in to finish - complete it in the other tab. This page updates automatically." />
          </p>
        )}
        <hr className="my-4 border-border" />
        {state.kind === 'updating-metadata' ? (
          <UpdateMetadataUrl resource={resource} onDone={() => setState({ kind: 'viewing' })} />
        ) : (
          <div className="flex flex-wrap gap-3">
            <Button
              color="primary"
              variant="outlined"
              size="sm"
              isLoading={state.kind === 'launching'}
              onClick={runTest}
              data-intercom-target="sso-manage-test-sign-in-button"
            >
              <T kind="Test sign-in" />
            </Button>
            <Button
              color="primary"
              variant="outlined"
              size="sm"
              onClick={() => setState({ kind: 'updating-metadata' })}
              data-intercom-target="sso-update-metadata-button"
            >
              <T kind="Update metadata URL" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default observer(ConnectionCard);
